import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Timeline as TimelineIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import Header from '../../components/Header';

const AnalysisHome = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 分析機能の選択肢
  const analysisOptions = [
    {
      title: "製品分析ダッシュボード",
      description: "製品別の月次供給量分析と顧客比較",
      icon: <BarChartIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      onClick: () => navigate("/analysis/product-dashboard"),
      color: "rgba(103, 126, 234, 0.1)",
      highlight: true, // メイン機能として強調
      badge: "おすすめ"
    },
    {
      title: "顧客製品詳細分析",
      description: "特定の顧客・製品組み合わせの詳細分析",
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      onClick: () => navigate("/analysis/client-product-select"),
      color: "rgba(156, 39, 176, 0.1)"
    }
  ];

  // 近日公開予定の分析機能
  const futureAnalysisFeatures = [
    {
      title: "予測分析",
      description: "AIによる供給量予測と補充推奨",
      icon: <AnalyticsIcon sx={{ fontSize: 40, color: 'text.secondary' }} />,
      disabled: true
    },
    {
      title: "比較分析",
      description: "複数製品・期間の比較分析",
      icon: <TimelineIcon sx={{ fontSize: 40, color: 'text.secondary' }} />,
      disabled: true
    },
    {
      title: "地域分析",
      description: "地域別の供給パターン分析",
      icon: <BusinessIcon sx={{ fontSize: 40, color: 'text.secondary' }} />,
      disabled: true
    }
  ];

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ 
        pt: 3, 
        pb: { xs: 12, sm: 6 },
        minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
      }}>
        {/* ヘッダーセクション */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AssessmentIcon sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              データ分析
            </Typography>
          </Box>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            供給データの詳細分析と統計情報
          </Typography>
        </Paper>

        {/* メイン分析機能 */}
        <Typography 
          variant="h6" 
          component="h2" 
          sx={{ mb: 3, fontWeight: 500, pl: 1 }}
        >
          分析機能
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {analysisOptions.map((option, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card 
                elevation={option.highlight ? 4 : 2}
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  border: option.highlight ? '2px solid' : 'none',
                  borderColor: option.highlight ? 'primary.main' : 'transparent',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: option.highlight ? 8 : 6
                  }
                }}
              >
                {/* バッジ表示 */}
                {option.badge && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      bgcolor: 'success.main',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      zIndex: 1
                    }}
                  >
                    {option.badge}
                  </Box>
                )}
                
                <CardActionArea 
                  onClick={option.onClick}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 3,
                    minHeight: { xs: 180, sm: 160 }
                  }}
                >
                  <Box 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: option.color,
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      mb: 2
                    }}
                  >
                    {option.icon}
                  </Box>
                  <Typography variant="h6" component="h3" sx={{ 
                    fontWeight: 500, 
                    textAlign: 'center',
                    mb: 1
                  }}>
                    {option.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    textAlign: 'center' 
                  }}>
                    {option.description}
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 近日公開予定の機能 */}
        <Box sx={{ mt: 5 }}>
          <Typography 
            variant="h6" 
            component="h2" 
            sx={{ mb: 3, color: 'text.secondary', fontWeight: 500, pl: 1 }}
          >
            近日公開予定の分析機能
          </Typography>
          
          <Grid container spacing={3}>
            {futureAnalysisFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  elevation={1}
                  sx={{ 
                    height: '100%',
                    opacity: 0.7,
                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                  }}
                >
                  <CardContent sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 3,
                    textAlign: 'center',
                    minHeight: { xs: 160, sm: 140 }
                  }}>
                    {feature.icon}
                    <Typography variant="h6" component="h3" sx={{ 
                      mt: 2, 
                      fontWeight: 500, 
                      color: 'text.secondary' 
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {feature.description}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 2, color: 'primary.main' }}>
                      近日公開予定
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default AnalysisHome;